
import { Building2, Calendar, Briefcase, Users, Globe, UserCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Industry, OrganizationSize } from "@/types/assessment";

interface ResultsHeaderProps {
  organizationName: string;
  assessmentDate: string;
  industry: Industry;
  organizationSize: OrganizationSize;
  country?: string;
  organizationType?: string;
  assessorName?: string;
  assessorRole?: string;
  assessorEmail?: string;
}

export const ResultsHeader = ({ 
  organizationName, 
  assessmentDate,
  industry,
  organizationSize,
  country,
  organizationType,
  assessorName,
  assessorRole
}: ResultsHeaderProps) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.getMonth() + 1;
    const monthStr = month.toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${monthStr}-${year}`;
  };

  return (
    <div className="space-y-6">
      {/* Organization Information */}
      <Card className="p-8 bg-white shadow-sm">
        <h3 className="text-2xl font-semibold text-[#1a237e] border-b pb-3 mb-6">Organization Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="flex items-center gap-4">
            <Building2 className="h-6 w-6 text-[#1a237e]" />
            <div>
              <p className="text-gray-500 text-base">Organization</p>
              <p className="text-gray-900 text-lg">{organizationName}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Briefcase className="h-6 w-6 text-[#1a237e]" />
            <div>
              <p className="text-gray-500 text-base">Industry</p>
              <p className="text-gray-900 text-lg">{industry}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Users className="h-6 w-6 text-[#1a237e]" />
            <div>
              <p className="text-gray-500 text-base">Size</p>
              <p className="text-gray-900 text-lg">{organizationSize}</p>
            </div>
          </div>

          {organizationType && (
            <div className="flex items-center gap-4">
              <Building2 className="h-6 w-6 text-[#1a237e]" />
              <div>
                <p className="text-gray-500 text-base">Organization Type</p>
                <p className="text-gray-900 text-lg">{organizationType}</p>
              </div>
            </div>
          )}

          {country && (
            <div className="flex items-center gap-4">
              <Globe className="h-6 w-6 text-[#1a237e]" />
              <div>
                <p className="text-gray-500 text-base">Country</p>
                <p className="text-gray-900 text-lg">{country}</p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Assessment Information */}
      <Card className="p-6 bg-white shadow-sm">
        <h3 className="text-xl font-semibold text-[#1a237e] border-b pb-2 mb-4">Assessment Details</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <Calendar className="h-5 w-5 text-[#1a237e] mt-1" />
            <div>
              <p className="text-sm text-gray-500">Assessment Date</p>
              <p className="text-base font-medium text-gray-900">{formatDate(assessmentDate)}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <UserCircle2 className="h-5 w-5 text-[#1a237e] mt-1" />
            <div>
              <p className="text-sm text-gray-500">Assessor</p>
              <p className="text-base font-medium text-gray-900">{assessorName || 'Not specified'}</p>
              <p className="text-sm text-gray-500">{assessorRole || 'Role not specified'}</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
